import { Component, OnInit } from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

// Angular Material
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DeleteDialogComponent } from '../delete/delete-dialog.component';
import { ResourceDialogComponent } from './resource-dialog.component';
import { CopyAvailabilityDialogComponent } from './copy-availability-dialog.component';
import { CopyResourceDialogComponent } from './copy-resource-dialog.component';
import { AvailabilityBlocksComponent } from './availability-blocks/availability-blocks.component';

interface Resource {
  id: string;
  name: string;
  external?: boolean;
  resourceTypeId: string;
  attributeValues?: ResourceAttributeValue[];
  availability?: any[];
  [key: string]: any;
}

interface ResourceType {
  id: string;
  name: string;
  attributes?: ResourceAttribute[];
  [key: string]: any;
}

interface ResourceAttribute {
  id: string;
  name: string;
  slug: string | null;
  type: string;
  external?: boolean;
  resourceTypeId: string;
  order: number | null;
  selectDisplayValues?: string | null;
  selectSlugValues?: string | null;
}

interface ResourceAttributeValue {
  id?: string;
  resourceAttributeId: string;
  resourceId: string;
  value: string;
}

@Component({
  selector: 'app-resources',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatTabsModule,
    MatSelectModule,
    MatSlideToggleModule,
    AvailabilityBlocksComponent,
  ],
  templateUrl: './resources.component.html',
  styleUrls: ['./resources.component.scss'],
})
export class ResourcesComponent implements OnInit {
  resources: Resource[] = [];
  resourceTypes: ResourceType[] = [];
  loading = false;
  error: string | null = null;

  displayedColumns: string[] = ['id', 'name', 'availability'];

  // Inline editing state
  editingCell: { resourceId: string; attributeId: string } | null = null;
  savingCell: { resourceId: string; attributeId: string } | null = null;

  // Cache select options for each attribute
  selectOptionsCache: { [attributeId: string]: { slug: string; display: string }[] } = {};

  private apiBaseUrl = environment.apiBaseUrl;
  private organisationId: string = '';
  selectedResourceTypeId: string = '';
  selectedResourceTypeTabIndex: number = 0;

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    // Get selected organisationId from localStorage
    const storedId = localStorage.getItem('selectedOrganisationId');
    if (!storedId) {
      this.error = 'No organisation selected.';
      return;
    }
    this.organisationId = storedId;
    this.fetchResourceTypes();

    // Add event listener for storage changes (in case another tab changes the selection)
    window.addEventListener('storage', (event) => {
      if (event.key === 'selectedOrganisationId') {
        if (event.newValue && event.newValue !== '') {
          this.organisationId = event.newValue;
          this.fetchResourceTypes();
        } else {
          this.error = 'No organisation selected.';
          this.resources = [];
          this.resourceTypes = [];
        }
      }
    });
  }

  fetchResourceTypes() {
    this.loading = true;
    this.error = null;

    this.http
      .get<ResourceType[]>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes`
      )
      .subscribe({
        next: (data) => {
          console.log('Fetched resource types:', data);
          this.resourceTypes = data;
          if (this.resourceTypes.length > 0) {
            // Check if there's a stored resource type ID
            const storedResourceTypeId = localStorage.getItem(
              'selectedResourceTypeId'
            );

            // Use the stored ID if it exists and is valid, otherwise use the first resource type
            let foundIdx = 0;
            if (storedResourceTypeId) {
              foundIdx = this.resourceTypes.findIndex(
                (rt) => rt.id.toString() === storedResourceTypeId.toString()
              );
              if (foundIdx === -1) {
                foundIdx = 0;
              }
              this.selectedResourceTypeId = this.resourceTypes[foundIdx].id;
            } else {
              this.selectedResourceTypeId = this.resourceTypes[0].id;
              foundIdx = 0;
              localStorage.setItem(
                'selectedResourceTypeId',
                this.selectedResourceTypeId
              );
            }
            // Set tab index for initial/preselect
            this.selectedResourceTypeTabIndex = foundIdx;

            // Update the displayed columns to include attributes
            this.updateDisplayedColumns();

            // Force change detection to update the UI before fetching resources
            setTimeout(() => {
              this.fetchResources(this.selectedResourceTypeId);
            }, 0);
          } else {
            this.loading = false;
            //this.error =  ('No resource types available. Please create a resource type first.');
          }
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.message;
          this.loading = false;
          this.snackBar.open('Failed to load resource types', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  fetchResources(resourceTypeId: string) {
    this.loading = true;
    // Make sure the selectedResourceTypeId is set correctly
    this.selectedResourceTypeId = resourceTypeId;
    console.log('fetchResources called with resourceTypeId:', resourceTypeId);
    console.log('selectedResourceTypeId set to:', this.selectedResourceTypeId);

    // First fetch the resource type to get its attributes
    this.http
      .get<ResourceType>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${resourceTypeId}`
      )
      .subscribe({
        next: (resourceType) => {
          // Store the resource type with its attributes
          const index = this.resourceTypes.findIndex(
            (rt) => rt.id === resourceTypeId
          );
          if (index !== -1) {
            this.resourceTypes[index] = resourceType;
          }

          // Update the displayed columns to include attributes
          this.updateDisplayedColumns();

          // Then fetch the resources
          this.http
            .get<Resource[]>(
              `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${resourceTypeId}/resources`
            )
            .subscribe({
              next: (data) => {
                // Sort resources by name alphabetically
                this.resources = data.sort((a, b) =>
                  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
                );

                // For each resource, fetch its attribute values and availability
                if (data.length > 0) {
                  // Create an array of promises for fetching attribute values and availability
                  const fetchPromises = data.map((resource) => {
                    const attrUrl = `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${resourceTypeId}/resources/${resource.id}/attributes`;
                    const availUrl = `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${resourceTypeId}/resources/${resource.id}/availabilities`;

                    console.log(`Fetching attributes from: ${attrUrl}`);

                    // Fetch both attributes and availability in parallel
                    return Promise.all([
                      this.http
                        .get<ResourceAttributeValue[]>(attrUrl)
                        .toPromise()
                        .then((values) => {
                          console.log(
                            `Received ${
                              values?.length || 0
                            } attribute values for resource ${resource.id}:`,
                            values
                          );
                          return values;
                        })
                        .catch((err) => {
                          console.error(
                            `Error fetching attributes for resource ${resource.id}:`,
                            err
                          );
                          return [] as ResourceAttributeValue[]; // Return empty array on error
                        }),
                      this.http
                        .get<any[]>(availUrl)
                        .toPromise()
                        .then((availability) => {
                          console.log(
                            `Received ${
                              availability?.length || 0
                            } availability slots for resource ${resource.id}`
                          );
                          return availability;
                        })
                        .catch((err) => {
                          console.error(
                            `Error fetching availability for resource ${resource.id}:`,
                            err
                          );
                          return []; // Return empty array on error
                        })
                    ]);
                  });

                  // Wait for all attribute value and availability requests to complete
                  Promise.all(fetchPromises)
                    .then((dataArray) => {
                      console.log(
                        'Attribute values and availability fetched:',
                        dataArray
                      );

                      // Assign attribute values and availability to each resource
                      this.resources = this.resources.map((resource, index) => {
                        const [attributeValues, availability] = dataArray[index] || [[], []];
                        return {
                          ...resource,
                          attributeValues: attributeValues || [],
                          availability: availability || [],
                        };
                      });

                      console.log('Resources with attributes:', this.resources);

                      // Debug attribute values
                      const attributes = this.getResourceTypeAttributes();
                      console.log('Resource type attributes:', attributes);

                      if (this.resources.length > 0 && attributes.length > 0) {
                        console.log('Sample attribute check:');
                        const sampleResource = this.resources[0];
                        attributes.forEach((attr) => {
                          console.log(
                            `Attribute ${attr.name} (ID: ${attr.id}):`,
                            this.getAttributeValue(sampleResource, attr.id)
                          );
                        });
                      }
                    })
                    .catch((err) => {
                      console.error('Error processing attribute values:', err);
                    })
                    .finally(() => {
                      this.loading = false;
                    });
                } else {
                  this.loading = false;
                }
              },
              error: (err: HttpErrorResponse) => {
                this.error = err.message;
                this.loading = false;
                this.snackBar.open('Failed to load resources', 'Close', {
                  duration: 3000,
                });
              },
            });
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.message;
          this.loading = false;
          this.snackBar.open('Failed to load resource type details', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  // Called when user clicks a tab
  onResourceTypeTabChange(tabIndex: number) {
    if (tabIndex < 0 || tabIndex >= this.resourceTypes.length) return;
    this.selectedResourceTypeTabIndex = tabIndex;
    const selected = this.resourceTypes[tabIndex];
    if (selected && selected.id !== this.selectedResourceTypeId) {
      this.selectedResourceTypeId = selected.id;
      localStorage.setItem(
        'selectedResourceTypeId',
        String(this.selectedResourceTypeId)
      );
      this.fetchResources(this.selectedResourceTypeId);
    }
  }

  getResourceTypeName(resourceTypeId: string): string {
    const resourceType = this.resourceTypes.find(
      (rt) => rt.id.toString() === resourceTypeId.toString()
    );
    return resourceType ? resourceType.name : 'Unknown';
  }

  getResourceTypeAttributes(): ResourceAttribute[] {
    if (!this.selectedResourceTypeId) return [];

    // Convert IDs to strings for comparison
    const selectedId = this.selectedResourceTypeId.toString();

    console.log('Looking for resource type with ID:', selectedId);
    console.log(
      'Available resource types:',
      this.resourceTypes.map((rt) => ({ id: rt.id, name: rt.name }))
    );

    const resourceType = this.resourceTypes.find(
      (rt) => rt.id.toString() === selectedId
    );

    if (!resourceType) {
      console.log('Resource type not found for ID:', selectedId);
      return [];
    }

    console.log('Found resource type:', resourceType.name);
    console.log('Resource type attributes:', resourceType.attributes);

    return resourceType.attributes ? resourceType.attributes : [];
  }

  getAttributeValue(resource: Resource, attributeId: string): string {
    if (!resource.attributeValues || !Array.isArray(resource.attributeValues)) {
      console.log(`No attribute values for resource ${resource.id}`);
      return '';
    }

    // Convert both IDs to strings for comparison
    const attributeIdStr = attributeId.toString();

    const attributeValue = resource.attributeValues.find((av) => {
      const avIdStr = av.resourceAttributeId.toString();
      const match = avIdStr === attributeIdStr;
      if (match) {
        console.log(
          `Found match for attribute ${attributeIdStr} with value: ${av.value}`
        );
      }
      return match;
    });

    if (!attributeValue) {
      console.log(
        `No value found for attribute ${attributeIdStr} in resource ${resource.id}`
      );
    }

    return attributeValue ? attributeValue.value : '';
  }

  updateDisplayedColumns() {
    // Start with the basic columns
    const columns = ['id', 'name', 'capacity', 'availability'];

    // Add attribute columns in the correct order
    const attributes = this.getResourceTypeAttributes();
    if (attributes && attributes.length > 0) {
      // Sort attributes by order before adding to columns
      const sortedAttributes = [...attributes].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      sortedAttributes.forEach((attr) => {
        columns.push('attr_' + attr.id);
      });

      console.log('Updated displayed columns with attributes:', columns);
    } else {
      console.log('No attributes found for resource type');
    }

    // Add actions column at the end
    columns.push('actions');

    this.displayedColumns = columns;
  }

  /**
   * For select attributes, get the display value for a given slug.
   */
  getSelectDisplayValue(
    attribute: ResourceAttribute,
    slug: string | undefined
  ): string {
    if (!attribute || attribute.type !== 'select' || !slug) return '';
    if (!attribute.selectDisplayValues || !attribute.selectSlugValues)
      return slug;

    const displayArr = attribute.selectDisplayValues
      .split(',')
      .map((s) => s.trim());
    const slugArr = attribute.selectSlugValues.split(',').map((s) => s.trim());
    const idx = slugArr.indexOf(slug);
    if (idx !== -1 && displayArr[idx] !== undefined) {
      return displayArr[idx];
    }
    return slug;
  }

  /**
   * Get select options for an attribute (cached)
   */
  getSelectOptions(attribute: ResourceAttribute): { slug: string; display: string }[] {
    if (!this.selectOptionsCache[attribute.id]) {
      this.selectOptionsCache[attribute.id] = this.computeSelectOptions(attribute);
    }
    return this.selectOptionsCache[attribute.id];
  }

  private computeSelectOptions(attribute: ResourceAttribute): { slug: string; display: string }[] {
    if (!attribute || attribute.type !== 'select') return [];
    if (!attribute.selectDisplayValues || !attribute.selectSlugValues) return [];
    const displayArr = attribute.selectDisplayValues.split(',').map(s => s.trim());
    const slugArr = attribute.selectSlugValues.split(',').map(s => s.trim());
    return slugArr.map((slug, idx) => ({
      slug,
      display: displayArr[idx] !== undefined ? displayArr[idx] : slug
    }));
  }

  // Inline editing methods
  isEditing(resourceId: string, attributeId: string): boolean {
    return this.editingCell?.resourceId === resourceId && this.editingCell?.attributeId === attributeId;
  }

  isSaving(resourceId: string, attributeId: string): boolean {
    return this.savingCell?.resourceId === resourceId && this.savingCell?.attributeId === attributeId;
  }

  startEditing(resourceId: string, attributeId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.editingCell = { resourceId, attributeId };
    // Focus the input after change detection
    setTimeout(() => {
      const input = document.querySelector('.inline-edit-container input') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  stopEditing() {
    this.editingCell = null;
  }

  cancelEditing(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.editingCell = null;
  }

  onAttributeChange(resource: Resource, attribute: ResourceAttribute, value: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.saveAttributeValue(resource, attribute, value);
  }

  onTextNumberBlur(resource: Resource, attribute: ResourceAttribute, value: string) {
    // Don't do anything if we're not editing (already cancelled)
    if (!this.editingCell) {
      return;
    }
    const currentValue = this.getAttributeValue(resource, attribute.id);
    this.stopEditing();
    // Only save if value changed
    if (value !== currentValue) {
      this.saveAttributeValue(resource, attribute, value);
    }
  }

  onTextNumberKeydown(event: KeyboardEvent, resource: Resource, attribute: ResourceAttribute) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const input = event.target as HTMLInputElement;
      this.onTextNumberBlur(resource, attribute, input.value);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.stopEditing();
    }
  }

  saveAttributeValue(resource: Resource, attribute: ResourceAttribute, value: string) {
    this.savingCell = { resourceId: resource.id, attributeId: attribute.id };

    this.http
      .post(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${resource.resourceTypeId}/resources/${resource.id}/attributes`,
        {
          resourceAttributeId: Number(attribute.id),
          value: value,
        }
      )
      .subscribe({
        next: () => {
          // Update local resource data
          if (!resource.attributeValues) {
            resource.attributeValues = [];
          }
          const existingValue = resource.attributeValues.find(
            (av) => av.resourceAttributeId.toString() === attribute.id.toString()
          );
          if (existingValue) {
            existingValue.value = value;
          } else {
            resource.attributeValues.push({
              resourceAttributeId: attribute.id,
              resourceId: resource.id,
              value: value,
            });
          }

          this.savingCell = null;
          this.snackBar.open('Attribute saved', 'Close', {
            duration: 2000,
          });
        },
        error: (err) => {
          console.error('Error saving attribute:', err);
          this.savingCell = null;
          this.snackBar.open('Failed to save attribute', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  hasExternalResources(): boolean {
    return this.resources.some(resource => resource.external === true);
  }

  openAddDialog() {
    if (!this.selectedResourceTypeId) {
      this.snackBar.open('Please select a resource type first', 'Close', {
        duration: 3000,
      });
      return;
    }

    const dialogRef = this.dialog.open(ResourceDialogComponent, {
      width: '600px',
      data: {
        name: '',
        resourceTypeId: this.selectedResourceTypeId,
        resourceTypes: this.resourceTypes,
        resourceType: this.resourceTypes.find(
          (rt) => rt.id === this.selectedResourceTypeId
        ),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name && result.resourceTypeId) {
        this.createResource(result);
      }
    });
  }

  openEditDialog(resource: Resource) {
    // Find the resource type with attributes
    const resourceType = this.resourceTypes.find(
      (rt) => rt.id === resource.resourceTypeId
    );

    const dialogRef = this.dialog.open(ResourceDialogComponent, {
      width: '600px',
      data: {
        ...resource,
        resourceTypes: this.resourceTypes,
        resourceType: resourceType,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name && result.resourceTypeId) {
        this.updateResource(resource, result);
      }
    });
  }

  manageAvailability(resource: Resource) {
    this.router.navigate([
      '/resources',
      resource.resourceTypeId,
      resource.id,
      'availability',
    ]);
  }

  openCopyAvailabilityDialog(resource: Resource) {
    // Find the resource type with attributes
    const resourceType = this.resourceTypes.find(
      (rt) => rt.id === resource.resourceTypeId
    );

    const dialogRef = this.dialog.open(CopyAvailabilityDialogComponent, {
      width: '90vw',
      maxWidth: '1600px',
      height: '85vh',
      maxHeight: '900px',
      data: {
        sourceResource: resource,
        resourceTypeId: resource.resourceTypeId,
        attributes: resourceType?.attributes || [],
        attributeValues: resource.attributeValues || []
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.refreshRequired) {
        // Refresh the resources list to show the updated data
        this.fetchResources(resource.resourceTypeId);
      }
    });
  }

  openCopyResourceDialog(resource: Resource) {
    // Find the resource type with attributes
    const resourceType = this.resourceTypes.find(
      (rt) => rt.id === resource.resourceTypeId
    );

    const dialogRef = this.dialog.open(CopyResourceDialogComponent, {
      width: '600px',
      data: {
        ...resource,
        sourceResourceId: resource.id,
        resourceTypes: this.resourceTypes,
        resourceType: resourceType,
        attributeValues: resource.attributeValues
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name && result.resourceTypeId) {
        this.copyResource(result);
      }
    });
  }

  createResource(newResource: Partial<Resource>) {
    this.http
      .post<Resource>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${newResource.resourceTypeId}/resources`,
        {
          name: newResource.name,
          capacity: newResource['capacity'] || 1,
          resourceTypeId: Number(newResource.resourceTypeId),
        }
      )
      .subscribe({
        next: (createdResource) => {
          // If there are attribute values, post them after resource creation
          if (
            newResource.attributeValues &&
            newResource.attributeValues.length > 0 &&
            createdResource &&
            createdResource.id
          ) {
            const attributePromises = newResource.attributeValues.map(
              (attrValue) => {
                return this.http
                  .post(
                    `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${newResource.resourceTypeId}/resources/${createdResource.id}/attributes`,
                    {
                      resourceAttributeId: Number(attrValue.resourceAttributeId),
                      value: attrValue.value,
                    }
                  )
                  .toPromise();
              }
            );

            Promise.all(attributePromises)
              .then(() => {
                this.snackBar.open('Resource and attributes created', 'Close', {
                  duration: 2000,
                });
                this.fetchResources(newResource.resourceTypeId as string);
              })
              .catch((err) => {
                console.error('Error creating attribute values:', err);
                this.snackBar.open(
                  'Resource created but some attributes failed to save',
                  'Close',
                  {
                    duration: 3000,
                  }
                );
                this.fetchResources(newResource.resourceTypeId as string);
              });
          } else {
            this.snackBar.open('Resource created', 'Close', {
              duration: 2000,
            });
            this.fetchResources(newResource.resourceTypeId as string);
          }
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to create resource', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  updateResource(resource: Resource, updatedData: Partial<Resource>) {
    // For external resources, update capacity (name is managed via SSO) and attribute values
    if (resource.external) {
      this.http
        .put<Resource>(
          `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${updatedData.resourceTypeId}/resources/${resource.id}`,
          {
            name: resource.name, // Keep original name for external resources
            capacity: updatedData['capacity'] || 1,
            resourceTypeId: Number(updatedData.resourceTypeId),
          }
        )
        .subscribe({
          next: () => {
            // Then update attribute values
            this.updateAttributeValuesOnly(resource, updatedData);
          },
          error: (err: HttpErrorResponse) => {
            this.error = err.error?.error || err.message;
            this.snackBar.open('Failed to update resource', 'Close', {
              duration: 3000,
            });
          },
        });
      return;
    }

    // For non-external resources, update basic info first
    this.http
      .put<Resource>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${updatedData.resourceTypeId}/resources/${resource.id}`,
        {
          name: updatedData.name,
          capacity: updatedData['capacity'] || 1,
          resourceTypeId: Number(updatedData.resourceTypeId),
        }
      )
      .subscribe({
        next: (updatedResource) => {
          // Then update attribute values if any
          if (
            updatedData.attributeValues &&
            updatedData.attributeValues.length > 0
          ) {
            const attributePromises = updatedData.attributeValues.map(
              (attrValue) => {
                return this.http
                  .post(
                    `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${updatedData.resourceTypeId}/resources/${resource.id}/attributes`,
                    {
                      resourceAttributeId: Number(
                        attrValue.resourceAttributeId
                      ),
                      value: attrValue.value,
                    }
                  )
                  .toPromise();
              }
            );

            Promise.all(attributePromises)
              .then(() => {
                this.snackBar.open('Resource and attributes updated', 'Close', {
                  duration: 2000,
                });
                this.fetchResources(updatedData.resourceTypeId as string);
              })
              .catch((err) => {
                console.error('Error updating attribute values:', err);
                this.snackBar.open(
                  'Resource updated but some attributes failed to update',
                  'Close',
                  {
                    duration: 3000,
                  }
                );
                this.fetchResources(updatedData.resourceTypeId as string);
              });
          } else {
            this.snackBar.open('Resource updated', 'Close', {
              duration: 2000,
            });
            this.fetchResources(updatedData.resourceTypeId as string);
          }
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to update resource', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  // Update only custom (non-external) attribute values for external resources
  private updateAttributeValuesOnly(resource: Resource, updatedData: Partial<Resource>) {
    if (!updatedData.attributeValues || updatedData.attributeValues.length === 0) {
      this.snackBar.open('No changes to save', 'Close', { duration: 2000 });
      return;
    }

    // Filter to only include custom (non-external) attributes
    const attributes = this.getResourceTypeAttributes();
    const customAttributeValues = updatedData.attributeValues.filter((attrValue) => {
      const attr = attributes.find(a => a.id.toString() === attrValue.resourceAttributeId.toString());
      return attr && !attr.external;
    });

    if (customAttributeValues.length === 0) {
      this.snackBar.open('No changes to save', 'Close', { duration: 2000 });
      return;
    }

    const attributePromises = customAttributeValues.map((attrValue) => {
      return this.http
        .post(
          `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${updatedData.resourceTypeId}/resources/${resource.id}/attributes`,
          {
            resourceAttributeId: Number(attrValue.resourceAttributeId),
            value: attrValue.value,
          }
        )
        .toPromise();
    });

    Promise.all(attributePromises)
      .then(() => {
        this.snackBar.open('Saved', 'Close', { duration: 2000 });
        this.fetchResources(updatedData.resourceTypeId as string);
      })
      .catch((err) => {
        console.error('Error updating attribute values:', err);
        this.snackBar.open('Failed to save', 'Close', { duration: 3000 });
        this.fetchResources(updatedData.resourceTypeId as string);
      });
  }

  copyResource(copyData: any) {
    // First create the resource
    this.http
      .post<Resource>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${copyData.resourceTypeId}/resources`,
        {
          name: copyData.name,
          capacity: copyData['capacity'] || 1,
          resourceTypeId: Number(copyData.resourceTypeId),
        }
      )
      .subscribe({
        next: (createdResource) => {
          const promises: Promise<any>[] = [];

          // Copy attribute values if any
          if (copyData.attributeValues && copyData.attributeValues.length > 0) {
            const attributePromises = copyData.attributeValues.map(
              (attrValue: any) => {
                return this.http
                  .post(
                    `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${copyData.resourceTypeId}/resources/${createdResource.id}/attributes`,
                    {
                      resourceAttributeId: Number(attrValue.resourceAttributeId),
                      value: attrValue.value,
                    }
                  )
                  .toPromise();
              }
            );
            promises.push(...attributePromises);
          }

          // Copy availability if requested
          if (copyData.copyAvailability && copyData.sourceAvailability && copyData.sourceAvailability.length > 0) {
            const availabilityPromises = copyData.sourceAvailability.map(
              (slot: any) => {
                return this.http
                  .post(
                    `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${copyData.resourceTypeId}/resources/${createdResource.id}/availabilities`,
                    {
                      resourceId: Number(createdResource.id),
                      dayOfWeek: slot.dayOfWeek,
                      startTime: slot.startTime,
                      endTime: slot.endTime
                    }
                  )
                  .toPromise();
              }
            );
            promises.push(...availabilityPromises);
          }

          // Wait for all operations to complete
          Promise.all(promises)
            .then(() => {
              this.snackBar.open('Resource copied successfully', 'Close', {
                duration: 2000,
              });
              this.fetchResources(copyData.resourceTypeId as string);
            })
            .catch((err) => {
              console.error('Error copying resource data:', err);
              this.snackBar.open(
                'Resource created but some data failed to copy',
                'Close',
                {
                  duration: 3000,
                }
              );
              this.fetchResources(copyData.resourceTypeId as string);
            });
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to copy resource', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  deleteResource(resource: Resource) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '350px',
      data: {
        title: 'Delete Resource',
        message: `Are you sure you want to delete resource "${resource.name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.http
          .delete(
            `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${resource.resourceTypeId}/resources/${resource.id}`
          )
          .subscribe({
            next: () => {
              this.snackBar.open('Resource deleted', 'Close', {
                duration: 2000,
              });
              this.fetchResources(resource.resourceTypeId);
            },
            error: (err: HttpErrorResponse) => {
              this.error = err.error?.error || err.message;
              this.snackBar.open('Failed to delete resource', 'Close', {
                duration: 3000,
              });
            },
          });
      }
    });
  }

  deleteAllAvailability() {
    const resourceTypeName = this.getResourceTypeName(this.selectedResourceTypeId);
    const resourceCount = this.resources.length;

    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete All Availability',
        message: `Are you sure you want to delete all availability for all ${resourceCount} resources of type "${resourceTypeName}"? This action cannot be undone.`,
        confirmText: 'Delete All',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.loading = true;

        this.http
          .delete<{ success: boolean; deletedCount: number }>(
            `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.selectedResourceTypeId}/availabilities`
          )
          .subscribe({
            next: (response) => {
              this.snackBar.open(
                `All availability deleted (${response.deletedCount} slots)`,
                'Close',
                { duration: 2000 }
              );
              this.fetchResources(this.selectedResourceTypeId);
            },
            error: (err) => {
              console.error('Error deleting availability:', err);
              this.snackBar.open('Failed to delete availability', 'Close', {
                duration: 3000,
              });
              this.loading = false;
            },
          });
      }
    });
  }
}
